// @ts-check
/* eslint-disable lodash/import-scope */
import moment from 'moment';
import {
  BRANCH_STATS_REGEX_FIELDS,
  GOALS_METRIC_NAMES,
  MEDIAN_METRICS,
  metricNames,
  TIME_TO_CUSTOM_STAGES_REGEX
} from '@linearb/linjs-common';
import AppRouter from 'common/AppRouter';
import { TRACKING, DASHBOARD_INTERNAL_TYPES } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import _ from 'lodash';
import QueryString from 'query-string';
import { GRANULARITY, FILTER_BY } from './consts';
import { METRIC_TYPE } from '../CustomReport/Widgets/consts/graphTypes';

const GOALS_VALUES = Object.values(GOALS_METRIC_NAMES);

export const isReportOrgLevelOnly = report => !!report?.is_org_level_only;

export const isMedianMetric = metricName =>
  MEDIAN_METRICS.includes(metricName) ||
  TIME_TO_CUSTOM_STAGES_REGEX.test(metricName);

/**
 * this function checks if a metric always goes to v3 without relying on the ff
 * @param {string} metricName
 * @param {boolean} isTeamGoals
 * @returns {boolean} true
 */
export const isAlwaysV3Metric = (metricName, isTeamGoals = false) =>
  GOALS_VALUES.includes(metricName) ||
  BRANCH_STATS_REGEX_FIELDS.some(rx => rx.test(metricName)) ||
  (isTeamGoals && metricName === metricNames.PR_MERGED); // when in team goals report, we should have pr.merged from v3

export const shouldFetchTotalsFromBackend = metricName =>
  isMedianMetric(metricName) ||
  [metricNames.PM_MTTR, metricNames.PM_CFR, metricNames.REVIEW_DEPTH].includes(
    metricName
  );

/**
 * @typedef {object} SelectedTeam
 * @property {number} id selected team id
 */

/**
 *
 * @param {object} param
 * @param {*} param.mapMetricPointToRedirectParams
 * @param {boolean} param.isQualityMetricsFlowFF
 * @param {boolean} param.isActivityReleasesEnabled
 * @param {*} param.enableIncidentsGraphClick
 * @param {*} param.mapMetricPointToGroupByRedirectParam
 * @param {object} param.filters
 * @param {*} param.filterByRootTeamId
 * @param {SelectedTeam} param.selectedTeam
 * @param {*} param.history
 * @param {() => void} param.selectTeamId
 * @returns {any}
 */
export const getOnPointClick =
  ({
    mapMetricPointToRedirectParams,
    isQualityMetricsFlowFF,
    isActivityReleasesEnabled,
    enableIncidentsGraphClick,
    mapMetricPointToGroupByRedirectParam,
    filters,
    filterByRootTeamId,
    selectedTeam,
    history,
    selectTeamId
  }) =>
  (graph, point) => {
    const { metricName } = graph;

    const redirectParams = _.get(
      mapMetricPointToRedirectParams(
        isQualityMetricsFlowFF,
        isActivityReleasesEnabled,
        enableIncidentsGraphClick
      ),
      metricName,
      () => {}
    )(point);

    if (!redirectParams) return;
    const groupByRedirectParams = mapMetricPointToGroupByRedirectParam(point);

    const {
      selectedRepo,
      selectedContributor,
      selectedTeams,
      selectedService,
      selectedLabels
    } = filters;

    /**
     * @type {string}
     */
    const clickedTeamId = _.get(groupByRedirectParams, 'teams', null);
    // If no root team and the team context from metrics is not the same as the global team (stored in redux)
    // We are changing the global team id to all teams in redux

    if (
      !filterByRootTeamId &&
      selectedTeam &&
      clickedTeamId !== selectedTeam.id.toString()
    ) {
      selectTeamId();
    }

    const shouldAddLabelsFilter = !!(
      redirectParams.path === AppRouter.PULL_REQUESTS.path &&
      selectedLabels.length
    );

    const qsParams = {
      ...redirectParams.qs,
      repositories:
        selectedRepo && selectedRepo.length ? selectedRepo.join(',') : null,
      author: _.get(selectedContributor, 'id', null),
      contributors: _.get(selectedContributor, 'id', null),
      teams: _.get(selectedTeams, '[0].id', null),
      services:
        selectedService && selectedService.length
          ? selectedService.join(',')
          : null,
      labels: shouldAddLabelsFilter ? selectedLabels.join(',') : null,
      ...groupByRedirectParams
    };

    const qs = _.omitBy(qsParams, _.isNil); // remove all undefined/null props

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.METRICS,
      click_type: TRACKING.CLICK.DRILLDOWN,
      element: _.get(graph, 'name', '')
    });

    history.push({
      pathname: redirectParams.path,
      search: QueryString.stringify(qs),
      state: _.get(redirectParams, 'state', {})
    });
  };

export const getGranularityByDates = ({
  start_date,
  end_date,
  selectedGranularity
}) => {
  const durationInDays = moment
    .duration(moment(end_date).diff(moment(start_date)))
    .asDays();

  const granularity =
    selectedGranularity !== GRANULARITY.AUTO
      ? selectedGranularity
      : durationInDays > 90
      ? GRANULARITY.MONTHS
      : durationInDays > 27
      ? GRANULARITY.WEEKS
      : GRANULARITY.DAYS;

  return granularity;
};

export const isFilteredByLabel = ({ filters, type, isInspection = false }) => {
  return (
    filters?.selectedLabels?.length > 0 &&
    type !== METRIC_TYPE.pm &&
    !(
      type === METRIC_TYPE.goals || type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS
    ) &&
    !isInspection
  );
};

const isFilteredByMultiTeams = ({ filters, type }) => {
  return (
    filters?.filterType === FILTER_BY.PEOPLE &&
    filters?.globallySelectedTeams?.length > 1 &&
    type !== METRIC_TYPE.pm &&
    !(
      type === METRIC_TYPE.goals || type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS
    )
  );
};

export const isShowTotal = ({ filters, type, isInspection }) => {
  const showTotal =
    !isFilteredByLabel({ filters, type, isInspection }) &&
    !isFilteredByMultiTeams({ filters, type });
  return showTotal;
};

export const getAreaSelectionPeriodFromEvent = e => {
  const startIdx = Math.round(e.xAxis[0].min);
  const endIdx = Math.round(e.xAxis[0].max);

  const xAxisCategories = e.xAxis[0].axis.categories;
  const after = xAxisCategories[startIdx]?.after || null;
  const before = xAxisCategories[endIdx]?.before || null;

  const isPeriodInvalid = !(
    moment(after).isValid() && moment(before).isValid()
  );

  return isPeriodInvalid ? null : { before, after };
};

/* istanbul ignore next */
export const getAreaSelectionPointsFromEvents = e => {
  const xAxis = e.xAxis[0];
  const yAxis = e.yAxis[0];

  const { min: xMin, max: xMax } = xAxis;
  const { min: yMin, max: yMax } = yAxis;

  const selectedPoints = [];
  e.target.series.forEach(series => {
    series.points.forEach(point => {
      const { x, y } = point;

      const isPointInsideArea =
        x >= xMin && x <= xMax && y >= yMin && y <= yMax;
      if (isPointInsideArea) {
        selectedPoints.push(point);
      }
    });
  });

  return selectedPoints;
};
